import { useTranslation } from "react-i18next";
import HeroButton from "/app/src/components/HeroUi/Button";
import {
  getProcessingStatusResponse,
  getWorkflowStatusResponse,
} from "/app/src/types/processing";
import { useCallback, useMemo, useState } from "react";
import { Form, Input, InputNumber, SubmitButton } from "formik-antd";
import GridWrapper from "/app/src/components/generic/gridWrapper";
import { Formik } from "formik";
import { processService, orderLineService } from "/app/src/services";
import { handlePromiseError } from "/app/src/helpers/api";
import {
  QueryObserverResult,
  useMutation,
  useQueryClient,
  useQuery,
} from "@tanstack/react-query";
import { Integration, Order, OrderLine } from "/app/src/models";
import { HeroChip } from "/app/src/components/HeroUi/Chip";
import { toast } from "sonner";
import { useOrderFields } from "/app/src/components/apps/orderBuilder/settings/fields/orderFieldsData";

/**
 * TaskProcessor
 *
 * Renders a task processing form and actions for a single processing workflow item.
 * The component coordinates local form state (via Formik) with server-side processing
 * operations (cancel, skip, confirm) and query-based data (selected order line fields).
 *
 * Props:
 * @param processingStatus - Current processing status object (may be undefined). Used to populate form values,
 *                           render remaining count and determine button disabled states.
 * @param processingId - The id of the currently active process (used by mutation calls). Nullable.
 * @param setProcessingId - Callback to update the active process id in parent state.
 * @param onStatusUpdate - Optional async callback invoked after skip/confirm to refresh processingStatus from parent.
 * @param order - The parent order object (used for directionType and for cache keys).
 * @param workflowStatus - Optional async callback to refresh workflow-level status; used after cancel/confirm to
 *                         retrieve and apply a new active process id.
 * @param setDisableProcessingStatus - Callback used to disable/enable processing status updates while a cancel
 *                                     mutation is in progress.
 * @param integration - Integration meta used to resolve order line field definitions.
 *
 * Returns:
 * @returns A JSX element containing the TaskProcessor form and action buttons.
 *
 * Errors and user feedback:
 * - If required order line fields are missing, confirming will be blocked and a toast.error will show a list
 *   of missing field names (or a generic message if none are available).
 * - Mutation errors are expected to be handled via handlePromiseError inside mutationFns; those errors will
 *   typically result in toasts or query-level error handling upstream.
 */
export default function TaskProcessor({
  processingStatus,
  processingId,
  setProcessingId,
  onStatusUpdate,
  order,
  workflowStatus,
  setDisableProcessingStatus,
  integration,
}: {
  processingStatus: getProcessingStatusResponse | undefined;
  processingId: string | null;
  setProcessingId: (id: string | null) => void;
  onStatusUpdate?: () => Promise<
    QueryObserverResult<getProcessingStatusResponse, unknown>
  >;
  order: Order;
  workflowStatus?: () => Promise<
    QueryObserverResult<getWorkflowStatusResponse, unknown>
  >;
  setDisableProcessingStatus: (value: boolean) => void;
  integration: Integration;
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { orderLineFields } = useOrderFields(integration.id, "line");

  const { data: selectedOrderLine } = useQuery({
    queryKey: ["orderLine", processingStatus?.masterOrderLineId],
    queryFn: () => {
      return orderLineService.getSingle(processingStatus?.masterOrderLineId);
    },
    enabled: Boolean(processingStatus?.masterOrderLineId),
    initialData: { order_line: {} },
    select: (data: { order_line: OrderLine }) => data.order_line,
  });

  const missingRequiredFields = useMemo(() => {
    // If we don't yet have the selected line, assume all required fields are missing
    if (!selectedOrderLine)
      return orderLineFields
        .filter((f) => f.required && f.name)
        .map((f) => f.name)
        .filter(Boolean);

    return orderLineFields
      .filter((f) => f.required && f.name)
      .filter((f) => {
        const fieldKey = f.name as keyof OrderLine;
        const value = selectedOrderLine?.[fieldKey];
        if (value === null || value === undefined) return true;
        if (typeof value === "string") return value.trim() === "";
        return false;
      })
      .map((f) => f.name)
      .filter(Boolean);
  }, [orderLineFields, selectedOrderLine]);

  const requiredFieldsMissing = missingRequiredFields.length > 0;

  const { mutateAsync: cancelProcessing } = useMutation({
    mutationFn: (processId: string) => {
      return processService.cancelProcess(processId).then(handlePromiseError);
    },
    onMutate: () => {
      setDisableProcessingStatus(true);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["startProcessing", order.id], {
        queued: false,
      });
      workflowStatus?.().then(() => {
        setProcessingId(data.activeProcessId);
      });
      toast.success("Processing cancelled successfully");
    },
    onSettled: () => {
      setDisableProcessingStatus(false);
    },
  });

  /**
   * Cancels the current processing task.
   * @returns {void}
   */
  const handleCancelProcessing = useCallback(() => {
    if (!processingId) return;
    cancelProcessing(processingId);
  }, [cancelProcessing, processingId]);

  const { mutateAsync: skipTask } = useMutation({
    mutationFn: (processId: string) => {
      return processService.skipTask(processId).then(handlePromiseError);
    },
    onSuccess: () => {
      setLoading(true);
      onStatusUpdate?.().then(() => {
        setLoading(false);
      });
    },
  });

  /**
   * Skips the current task in processing.
   * @returns {void}
   */
  const handleSkipTask = useCallback(() => {
    if (!processingId) return;
    skipTask(processingId);
  }, [processingId, skipTask]);

  const { mutateAsync: confirmTask } = useMutation({
    mutationFn: ({
      processId,
      quantity,
    }: {
      processId: string;
      quantity: number;
    }) => {
      return processService
        .confirmTask(processId, quantity)
        .then(handlePromiseError);
    },
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: (data) => {
      setLoading(true);
      onStatusUpdate?.().then(() => {
        setLoading(false);
      });
      workflowStatus?.().then(() => {
        setProcessingId(data.activeProcessId);
      });
      toast.success("Task confirmed successfully");
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  /**
   * Submits the confirmation form for the current task.
   * @param {Object} values - Form values.
   * @param {string} values.locationName - Name of the task location.
   * @param {number} values.value - Quantity value.
   * @returns {Promise<void>}
   */
  const handleSubmit = useCallback(
    async (values: { locationName: string; value: number }) => {
      if (!processingId) return;
      if (requiredFieldsMissing) {
        const list = missingRequiredFields.join(", ");
        toast.error(
          list
            ? `Please update required line fields before confirming: ${list}`
            : "Please update required line fields before confirming.",
        );
        return;
      }
      await confirmTask({
        processId: processingId,
        quantity: Number(values.value),
      });
    },
    [processingId, confirmTask, requiredFieldsMissing, missingRequiredFields],
  );

  /**
   * Renders the task processing form layout.
   * @returns {JSX.Element} Form component.
   */
  const taskForm = useCallback(() => {
    return (
      <Form layout="vertical">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <h2 className="text-[18px] mb-0">
              {t("translation:task_processor")}
            </h2>
            <HeroChip color="primary">
              {t("translation:remaining_tasks")}:{" "}
              {processingStatus?.remainingTasks || 0}
            </HeroChip>
          </div>
          <div className="flex space-x-4">
            <HeroButton
              onClick={handleCancelProcessing}
              color="danger"
              variant="bordered"
              isDisabled={loading || processingStatus?.status === "Busy"}
            >
              {t("translation:cancel")}
            </HeroButton>
            <HeroButton
              onClick={handleSkipTask}
              color="default"
              variant="bordered"
              isDisabled={loading || processingStatus?.status === "Busy"}
            >
              {t("translation:skip")}
            </HeroButton>
            <SubmitButton
              size="large"
              color="primary"
              disabled={loading || processingStatus?.status === "Busy"}
            >
              {t("translation:confirm")}
            </SubmitButton>
          </div>
        </div>
        <GridWrapper responsive={{ sm: 1, md: 2, lg: 3 }}>
          <Form.Item
            hasFeedback={false}
            name="materialName"
            label={t("translation:material_name")}
          >
            <Input name="materialName" size="large" disabled />
          </Form.Item>
          <Form.Item
            hasFeedback={false}
            name="locationName"
            label={t("translation:location")}
          >
            <Input name="locationName" size="large" disabled />
          </Form.Item>
          <Form.Item
            hasFeedback={false}
            name="value"
            label={t("translation:quantity")}
          >
            <InputNumber
              name="value"
              min={order?.directionType === 5 ? 0 : 1}
              size="large"
            />
          </Form.Item>
        </GridWrapper>
      </Form>
    );
  }, [
    handleCancelProcessing,
    handleSkipTask,
    loading,
    order?.directionType,
    processingStatus?.remainingTasks,
    processingStatus?.status,
    t,
  ]);

  return (
    <div>
      <Formik
        component={taskForm}
        initialValues={{
          locationName: processingStatus?.locationName || "",
          value: Number(processingStatus?.value) || 0,
          materialName: processingStatus?.materialName || "",
        }}
        enableReinitialize
        onSubmit={handleSubmit}
      />
    </div>
  );
}
